// @flow

import React, {useCallback, useState, useEffect} from 'react';
import {
  Divider,
  IconButton,
  makeStyles,
  Typography,
  CircularProgress,
  Tooltip,
  FormControlLabel,
} from '@material-ui/core';

import {Add, Edit} from '@material-ui/icons';

import {colors} from '../../../../../../assets/styles/colors';
import {useStoreCurrency} from '../../../../../currency/utils';
import {commaFormat} from '../../../../../../utils/number';
import ConversationLabel from '../ConversationLabel/ConversationLabel';
import HorizontalScroll from '../../../../../../elements/HorizontalFilter/HorizontalScroll/HorizontalScroll';
import AddConversationLabelModal from '../../Modals/AddConversationLabelModal';
import EditCustomerModal from '../../Modals/EditCustomerModal';
import MergeContactDetailsModal from '../../Modals/MergeContactDetailsModal';
import {
  useCurrentConversation,
  getConversationDisplayName,
  isValidDisplayIdentifierOrPhone,
  useConversationChatbot,
} from '../../../utils/conversation';
import {useSetTagToConversation} from '../../../utils/conversation';
import {formatPhoneNumber} from '../../../../../../utils/phone';
import {useCollaborators} from '../../../../../configure/accounts/utils';
import LogoutIcon from '../../../../../../elements/icons/LogoutIcon';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import UserTickIcon from '../../../../../../elements/icons/UserTickIcon';
import CallIcon from '../../../../../../elements/icons/CallIcon';
import SmsSearchIcon from '../../../../../../elements/icons/SmsSearchIcon';
import TagIcon from '../../../../../../elements/icons/TagIcon';
import TicketMoneyIcon from '../../../../../../elements/icons/TicketMoneyIcon';
import MoneyReceivedIcon from '../../../../../../elements/icons/MoneyReceivedIcon';
import ShoppingCartIcon from '../../../../../../elements/icons/ShoppingCartIcon';
import ContactInformationSection from './sections/ContactInformation';
import SectionCollapse from './sections/SectionCollapse';
import SellInfo from './sections/SellInfo';
import ScheduleMessageList from './sections/ScheduleMessageList';
import ConversationNotesSection from '../../../../notes/components/ConversationNotesSection';
import FunnelSection from './sections/FunnelSection';
import {classNames} from '../../../../../../utils/react';
import BotIcon from '../../../../../../elements/icons/BotIcon';
import IosSwitch from '../../../../../../elements/IosSwitch/IosSwitch';
import {useToastActions} from '../../../../../toast-alert/utils';
import {useStoreDevices} from '../../../utils';

type Props = {
  expanded: boolean,
  toggleExpanded: () => void,
};
const useStyles = makeStyles(theme => ({
  root: {
    padding: '.5rem',
    [theme.breakpoints.up('lg')]: {
      padding: '.75rem',
    },
    height: '100%',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },

    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },
  },
  collapsedContent: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    flexDirection: 'column',
    gap: '.5rem',
  },
  content: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    zIndex: 10,
    maxWidth: '300px',
    overflow: 'auto',
    background: 'rgb(255 255 255 / 80%)',
    backdropFilter: 'blur(66px)',
    padding: '1rem',
    borderRadius: '0 12px 12px 0',
    boxShadow: '-1rem 0 3rem -2rem #0000003b',

    [theme.breakpoints.up('lg')]: {
      maxWidth: '100%',
      position: 'relative',
      padding: '0',
      borderRadius: '0',
      boxShadow: 'none',
      backdropFilter: 'none',
      overflow: 'initial',
    },
  },
  contentEmpty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  innerDivider: {
    border: '1px dashed #E8E8ED',
    backgroundColor: 'unset',
    width: '100%',
  },
  title: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 600,
  },
  subtitle: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 500,
  },
  assignedName: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 500,
    padding: '.13rem .5rem',
    borderRadius: '.5rem',
    background: colors.grey200,
  },
  greyText: {
    color: colors.grey700,
    fontSize: 14,
    fontWeight: 400,
  },
  section: {
    padding: theme.spacing(2, 0),
  },
  row: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  iconButton: {},
  horizontalScroll: {
    paddingBottom: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  tagConversationLoader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#ffffff80',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shortNameWrap: {
    background: '#E5DCFC',
    height: 56,
    width: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  },
  header: {
    display: 'flex',
    paddingBottom: '1rem',
    alignItems: 'center',
    flexDirection: 'column',
    gap: '.25rem',

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
  },
  floatEdit: {
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column-reverse',
    gap: '.5rem',

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
  },
  expandInboxButton: {
    padding: '.5rem',
    '&[data-expanded="true"]': {
      transform: 'rotate(180deg)',
    },
  },
  tooltip: {
    background: colors.grey700,
    boxShadow: '0px 4px 12px rgba(14, 21, 58, 0.08)',
    borderRadius: '4px',
    padding: '.5rem .75rem',
    color: 'white',
    fontSize: '.85rem',
    fontWeight: 500,
  },
  arrow: {
    color: colors.grey700,
  },
  profileIconItem: {
    color: colors.grey700,
    fontSize: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '.5rem',
  },
  icon: {
    color: colors.grey700,
  },
  enabled: {
    color: colors.success,
  },
}));

export default function ContactProfile(props: Props) {
  const {expanded, toggleExpanded} = props;

  const classes = useStyles();
  const currency = useStoreCurrency();

  const {showToast} = useToastActions();
  const {storeDevices} = useStoreDevices();

  const {collaborators} = useCollaborators();

  const {currentConversation: conversation} = useCurrentConversation();
  const {setTagToConversation, loading} = useSetTagToConversation(
    conversation ? conversation.id : ''
  );

  const [openMergeContactModal, setOpenMergeContactModal] = useState(false);
  const [openContactModal, setOpenContactModal] = useState(false);
  const [openLabelModal, setOpenLabelModal] = useState(false);

  const {toggleChatbotEnabled} = useConversationChatbot(conversation?.id);

  const [botEnabled, setBotEnabled] = useState(!conversation?.disabledChatbot);

  useEffect(() => {
    if (!conversation) return;
    setBotEnabled(!conversation?.disabledChatbot);
  }, [conversation]);

  const handleCloseLabelModal = useCallback(() => {
    setOpenLabelModal(false);
  }, []);

  const handleCloseCustomerModal = useCallback(() => {
    setOpenContactModal(false);
  }, []);

  const handleCloseMergeModal = useCallback(() => {
    setOpenMergeContactModal(false);
  }, []);

  if (!conversation) {
    return (
      <div className={classes.root}>
        <div className={classes.contentEmpty}>
          {expanded ? 'Seleccione una conversación' : ''}
        </div>
      </div>
    );
  }
  const {
    crmCustomer,
    tags = [],
    contactInformation,
    userId,
    userType,
    crmConversationState,
  } = conversation;

  const handleRemoveTag = (tagId: string) => {
    const newTags = tags
      .filter(tag => tag.id !== tagId)
      .map(tag => tag.id.replace('CT', 'CTA'));
    setTagToConversation(newTags);
  };

  const displayName = getConversationDisplayName(conversation);
  const shortName = displayName.substring(0, 2).toUpperCase();

  const getAssignedName = (): string | false => {
    if (userType === 'VENDOR') return 'Administrador';
    if (userType === 'COLLABORATOR') {
      const collaborator = collaborators.find(
        collaborator => collaborator.id === userId?.replace('U', 'CU')
      );
      if (collaborator) return collaborator.firstName;
    }

    return false;
  };

  const assignedName = getAssignedName();

  const customerPhone =
    contactInformation &&
    contactInformation.phone &&
    isValidDisplayIdentifierOrPhone(contactInformation.phone)
      ? formatPhoneNumber(contactInformation.phone)
      : null;
  const customerEmail = crmCustomer && crmCustomer.email;

  const handleToggleBot = () => {
    toggleChatbotEnabled(!!botEnabled)
      .then(() => {
        setBotEnabled(prev => !prev);
      })
      .catch(() => {
        showToast({
          message: 'Error al cambiar el estado del chatbot',
          title: 'Error',
          type: 'danger',
        });
      });
  };

  const botActive = !!storeDevices.find(
    device =>
      device.id === conversation.deviceId &&
      device.state === 'CONNECTED' &&
      device.chatbotId
  );

  return (
    <div className={classes.root}>
      {!expanded && (
        <div className={classes.collapsedContent}>
          <Tooltip
            title="Info. Cliente"
            arrow
            placement="left"
            classes={{
              tooltip: classes.tooltip,
              arrow: classes.arrow,
            }}
          >
            <IconButton
              size="small"
              onClick={toggleExpanded}
              data-expanded={expanded}
              className={classes.expandInboxButton}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={displayName}
            arrow
            placement="left"
            classes={{
              tooltip: classes.tooltip,
              arrow: classes.arrow,
            }}
          >
            <div className={classes.shortNameWrap}>
              <Typography
                color="primary"
                style={{fontWeight: 500, fontSize: 18}}
              >
                {shortName}
              </Typography>
            </div>
          </Tooltip>
          <Tooltip
            title={assignedName ? `Asignado a ${assignedName}` : 'No asignado'}
            arrow
            placement="left"
            classes={{
              tooltip: classes.tooltip,
              arrow: classes.arrow,
            }}
          >
            <span className={classes.profileIconItem}>
              <UserTickIcon />
            </span>
          </Tooltip>
          <Divider className={classes.innerDivider} />
          {customerPhone && (
            <Tooltip
              title={customerPhone}
              arrow
              placement="left"
              classes={{
                tooltip: classes.tooltip,
                arrow: classes.arrow,
              }}
            >
              <span className={classes.profileIconItem}>
                <CallIcon />
              </span>
            </Tooltip>
          )}
          {customerEmail && (
            <Tooltip
              title={customerEmail}
              arrow
              placement="left"
              classes={{
                tooltip: classes.tooltip,
                arrow: classes.arrow,
              }}
            >
              <span className={classes.profileIconItem}>
                <SmsSearchIcon />
              </span>
            </Tooltip>
          )}
          <Divider className={classes.innerDivider} />

          <Tooltip
            title="Crear Etiqueta"
            arrow
            placement="left"
            classes={{
              tooltip: classes.tooltip,
              arrow: classes.arrow,
            }}
          >
            <IconButton
              size="small"
              color="primary"
              onClick={() => setOpenLabelModal(true)}
            >
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>

          {crmCustomer && (
            <>
              <Divider className={classes.innerDivider} />
              <Tooltip
                title={`Categoría: ${crmCustomer.crmCustomerCategory}`}
                arrow
                placement="left"
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.arrow,
                }}
              >
                <span className={classes.profileIconItem}>
                  <TagIcon />
                </span>
              </Tooltip>
              <Tooltip
                title={`Ticket Promedio: ${commaFormat(
                  crmCustomer.averageOrderAmount,
                  currency ? currency.symbol : ''
                )}`}
                arrow
                placement="left"
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.arrow,
                }}
              >
                <span className={classes.profileIconItem}>
                  <TicketMoneyIcon />
                </span>
              </Tooltip>
              <Tooltip
                title={`Compras totales: ${commaFormat(
                  crmCustomer.totalOrderAmount,
                  currency ? currency.symbol : ''
                )}`}
                arrow
                placement="left"
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.arrow,
                }}
              >
                <span className={classes.profileIconItem}>
                  <MoneyReceivedIcon />
                </span>
              </Tooltip>
              <Tooltip
                title={`Ordenes ${crmCustomer.numberOfOrders}`}
                arrow
                placement="left"
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.arrow,
                }}
              >
                <span className={classes.profileIconItem}>
                  <ShoppingCartIcon />
                </span>
              </Tooltip>
            </>
          )}
        </div>
      )}
      {expanded && (
        <div className={classes.content} data-expanded={expanded}>
          <div className={classes.header}>
            <div>
              <div className={classes.shortNameWrap}>
                <Typography
                  color="primary"
                  style={{fontWeight: 500, fontSize: 18}}
                >
                  {shortName}
                </Typography>
              </div>
            </div>
            <div>
              <Typography className={classes.title}>{displayName}</Typography>
              <Typography className={classes.greyText}>
                ID:{conversation.id}
              </Typography>
            </div>
            <div className={classes.floatEdit}>
              <IconButton
                onClick={() => setOpenContactModal(true)}
                className={`${classes.iconButton} ${classes.floatIcon}`}
                size="small"
              >
                <Edit fontSize="small" />
              </IconButton>
              <Tooltip
                title="Ocultar"
                arrow
                placement="left"
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.arrow,
                }}
              >
                <IconButton
                  size="small"
                  onClick={toggleExpanded}
                  data-expanded={expanded}
                  className={classes.expandInboxButton}
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <Divider className={classes.innerDivider} />
          <div className={classes.section}>
            <div className={classes.row} style={{margin: 0}}>
              <Typography className={classes.subtitle}>Asignado a:</Typography>
              {assignedName ? (
                <Typography className={classes.assignedName}>
                  {assignedName}
                </Typography>
              ) : (
                <Typography className={classes.subtitle}>
                  No asignado
                </Typography>
              )}
            </div>
          </div>
          {botActive && (
            <>
              <Divider className={classes.innerDivider} />

              <FormControlLabel
                control={
                  <IosSwitch
                    checked={botEnabled}
                    onChange={handleToggleBot}
                    size="small"
                  />
                }
                onClick={e => e.stopPropagation()}
                labelPlacement="start"
                className={classes.row}
                style={{margin: 0}}
                label={
                  <div className={classes.row}>
                    <BotIcon
                      width="24"
                      height="24"
                      className={classNames(classes.icon, {
                        [classes.enabled]: botEnabled,
                      })}
                    />
                    <Typography
                      className={classNames(classes.subtitle, {
                        [classes.enabled]: botEnabled,
                      })}
                    >
                      Activar Chatbot
                    </Typography>
                  </div>
                }
              />
            </>
          )}
          <Divider className={classes.innerDivider} />
          {(contactInformation || crmCustomer) && (
            <SectionCollapse label="Información de contacto">
              <ContactInformationSection
                contactInformation={contactInformation}
                crmCustomer={crmCustomer}
              />
            </SectionCollapse>
          )}

          <Divider className={classes.innerDivider} />

          <div className={classes.section}>
            <div className={classes.row} style={{margin: 0}}>
              <Typography className={classes.subtitle}>
                Etiquetas de conversación
              </Typography>
              <CustomButton
                variant="text"
                color="primary"
                onClick={() => setOpenLabelModal(true)}
              >
                <Add fontSize="small" />
                Agregar
              </CustomButton>
            </div>
            <HorizontalScroll className={classes.horizontalScroll}>
              {loading && (
                <div className={classes.tagConversationLoader}>
                  <CircularProgress size={24} />
                </div>
              )}
              {tags?.map(tag => (
                <ConversationLabel
                  key={tag.id}
                  label={tag.tag}
                  disabled={loading}
                  onDelete={() => {
                    handleRemoveTag(tag.id);
                  }}
                />
              ))}
            </HorizontalScroll>
          </div>

          <Divider className={classes.innerDivider} />

          {crmConversationState === 'OPEN' && (
            <SectionCollapse label="Embudo de venta">
              <FunnelSection
                conversationFunnel={conversation?.funnel?.name}
                conversationId={conversation.id}
              />
            </SectionCollapse>
          )}

          {!!crmCustomer ? (
            <SectionCollapse label="Datos de compra">
              <SellInfo conversation={conversation} crmCustomer={crmCustomer} />
            </SectionCollapse>
          ) : (
            <div className={classes.section}>
              <Typography className={classes.subtitle}>
                No hay datos de compra para este número
              </Typography>
            </div>
          )}

          <SectionCollapse label="Mensajes Programados">
            <ScheduleMessageList />
          </SectionCollapse>

          <SectionCollapse label="Notas">
            <ConversationNotesSection conversationId={conversation.id} />
          </SectionCollapse>
        </div>
      )}

      <EditCustomerModal
        open={openContactModal}
        handleClose={handleCloseCustomerModal}
      />
      {openLabelModal && (
        <AddConversationLabelModal
          open={openLabelModal}
          //$FlowFixMe
          tags={tags}
          onSave={params => {
            setTagToConversation(params.map(tag => tag.id));
          }}
          handleClose={handleCloseLabelModal}
        />
      )}
      <MergeContactDetailsModal
        open={openMergeContactModal}
        handleClose={handleCloseMergeModal}
      />
    </div>
  );
}
